'use client';

import { Check, Copy } from 'lucide-react';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button, type ButtonProps } from './button';
import type { ButtonSize, ButtonVariant } from './button-class';

export interface UseClipboardReturn {
  /** The id of the value copied last, or null once the acknowledgement is over. */
  copiedId: string | null;
  /** True while a copy is acknowledged. */
  copied: boolean;
  /**
   * Copies `text`; `id` tells apart the values of a component that copies several (compare
   * `copiedId === id`). Resolves to false when the browser refuses (no permission, an
   * insecure page), leaving the state as it was.
   */
  copy: (text: string, id?: string) => Promise<boolean>;
}

/**
 * Copy to the clipboard with an acknowledgement that ends by itself after `resetMs` (2 s by
 * default). A new copy restarts it.
 */
export function useClipboard(resetMs = 2000): UseClipboardReturn {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (text: string, id = 'default') => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        return false;
      }
      setCopiedId(id);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        setCopiedId(null);
      }, resetMs);
      return true;
    },
    [resetMs],
  );

  return { copiedId, copied: copiedId !== null, copy };
}

export type CopyButtonProps = Omit<ButtonProps, 'children' | 'asChild' | 'pending'> & {
  /** What goes to the clipboard. */
  text: string;
  /** The button's text at rest (« Copier »). */
  label?: ReactNode;
  /** Its text right after a copy (« Copié »), also announced to screen readers. */
  copiedLabel?: ReactNode;
  /** The icon alone: give the button an `aria-label`. */
  iconOnly?: boolean;
  variant?: ButtonVariant;
  /** `sm` by default, `icon-sm` with `iconOnly`. */
  size?: ButtonSize;
  /** How long the acknowledgement lasts, in ms. */
  resetMs?: number;
  /** After a successful copy. */
  onCopied?: () => void;
};

/**
 * Copies a value: the copy icon and « Copier », then a check and « Copié » for a moment. The
 * acknowledgement is announced (a polite status beside the button). A click calls `onClick`
 * first, so a button inside a clickable row can stop the event there, or cancel the copy with
 * `preventDefault`.
 */
export function CopyButton({
  text,
  label = 'Copier',
  copiedLabel = 'Copié',
  iconOnly = false,
  variant = 'secondary',
  size,
  resetMs,
  onCopied,
  onClick,
  ...props
}: CopyButtonProps) {
  const { copied, copy } = useClipboard(resetMs);
  return (
    <>
      <Button
        variant={variant}
        size={size ?? (iconOnly ? 'icon-sm' : 'sm')}
        onClick={async (event: MouseEvent<HTMLButtonElement>) => {
          onClick?.(event);
          if (event.defaultPrevented) return;
          if (await copy(text)) onCopied?.();
        }}
        {...props}
      >
        {copied ? <Check aria-hidden className="text-crow-success" /> : <Copy aria-hidden />}
        {iconOnly ? null : <span>{copied ? copiedLabel : label}</span>}
      </Button>
      <span role="status" className="sr-only">
        {copied ? copiedLabel : null}
      </span>
    </>
  );
}
